import { graphqlOperation } from "@aws-amplify/api";
import AsyncStorage from "@react-native-async-storage/async-storage";
import useApi from "@toothfairy/shared-api/useApi";
import AppBaseScreen from "@toothfairy/shared-ui/AppBaseScreen";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppNotification from "../Hooks/useNotification";
import AppUser from "@toothfairy/shared-ui/AppUser";
import dayjs from "dayjs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Platform, StyleSheet, View, useWindowDimensions } from "react-native";
import { makeVar, useReactiveVar } from "react-reactive-var";
import { getWebAppUrl } from "../Utils/regionWebUrl";
import {
  currentUserId,
  forcedClosed,
  isConnectedToCloud,
  isElectronRunning,
  pinnedSideMenu,
  showSideMenu,
  userNotifications,
  userType,
} from "../../App";
import envConfig from "../../envConfig";
import { makeGraphQLRequest, subscribeGraphQL } from "../API";
import appConfigHelper from "../API/AppConfig/appConfigHelper";
import batchLogHelper from "../API/Batch/batchLogHelper";
import benchmarksHelper from "../API/Benchmarks/benchmarksHelper";
import entitiesHelper from "../API/EntitiesAI/entitiesHelper";
import helper from "../API/GenerationConfig/helper";
import Languages from "../API/Languages";
import Prompts from "../API/Prompts";
import trainingAPI from "../API/Training/trainingAPI";
import trainingLogHelper from "../API/TrainingLog/trainingLogHelper";
import usersAPI from "../API/Users/usersAPI";
import virtualMachineHelper from "../API/VirtualMachines/virtualMachinesHelper";
import workspacesAPI from "../API/Workspaces/workspacesAPI";
import workspacesHelper from "../API/Workspaces/workspacesHelper";
import ToothFairyLoadingPlaceholder from "../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import Constants from "../Constants";
import useWorkspaces from "../Hooks/useWorkspaces";
import { DeepLinking, Router } from "../Router";
import socket from "../Socket";
import { onUpdateWorkspaceSocket } from "../graphql/custom";
import { userToWorkspaceMapping } from "../graphql/queries";
import {
  onCreateBatchJobLog,
  onCreateUserNotification,
  onUpdateBatchJobLog,
  onUpdateSite,
  onUpdateTrainingLog,
} from "../graphql/subscriptions";
import { changeLanguage } from "../../i18n.config";
import { UserType } from "../models";
import MainViewScreen from "./MainViewScreen";
import OnboardingScreen from "./Onboarding/OnboardingScreen";
import customExecutionContainersHelper from "../API/CustomExecutionContainers/customExecutionContainersHelper";

export const appDimensions = makeVar({ width: 0, height: 0 });
export const lastMembersChange = makeVar(dayjs().toDate().getTime());
export const globalAppConfig = makeVar({});
export const isLargeScreen = makeVar(true);
export const isMobileScreen = makeVar(false);
export const isLandscapeMobile = makeVar(false);
export const isVeryLargeScreen = makeVar(false);
export const isLargerScreen = makeVar(false);
export const minAppWidth = makeVar(365);
const listPredicate = (c, list, field) => {
  return c.or((c) => list.reduce((_c, operand) => _c[field]("eq", operand), c));
};

const MainViewContainer = ({
  isLocalDBReady,
  children,
  authUserSub,
  serverWorkspaces,
  currentView,
  stripeCustomerData,
  _isInitialisationInProgress,
}) => {
  const userID = useReactiveVar(currentUserId);
  const _isConnectedToCloud = useReactiveVar(isConnectedToCloud);
  const _lastMembersChange = useReactiveVar(lastMembersChange);
  const AppToasty = AppToast.useToast();
  const { t, i18n } = useTranslation();
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const { setMode, mode } = AppTheme.useTheme();
  const { height, width } = useWindowDimensions();
  const {
    getActiveWorkspace,
    updateWorkspaceInContext,
    isUserAdmin,
    loadAgents,
  } = useWorkspaces();
  const {
    data: languagesData,
    error: languagesError,
    loading: languagesRequestInProgress,
    apiRequest: languagesRequestFetchRequest,
    response: languagesRequestResponse,
  } = useApi(Languages.getAvailableLanguages, envConfig);
  const {
    loading: documentFetchInProgress,
    apiRequest: fetchMRCData,
    wasInvoked: fetchMRCDataInvoked,
  } = useApi(trainingAPI.getDocumentsByWorkspace, null, () => {});
  isLargeScreen(width > (globalAppConfig()?.screenResponsiveWidth || 768));
  isLargerScreen(
    width > (globalAppConfig()?.largerScreenResponsiveWidth || 1024)
  );
  isMobileScreen(width < (globalAppConfig()?.mobileResponsiveWidth || 420));
  isLandscapeMobile(width < (globalAppConfig()?.mobileResponsiveWidth || 550));
  isVeryLargeScreen(
    width > (globalAppConfig()?.veryLargeScreenResponsiveWidth || 1200)
  );
  const fetchAppConfig = useCallback(async () => {
    if (isLocalDBReady) {
      // wait 2 seconds for local db to be ready
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const result = await appConfigHelper.listAppConfig();
      if (result.length === 0) {
        throw new Error("App config not found");
      }
      globalAppConfig(result[0]);
    }
  }, [isLocalDBReady]);
  useEffect(() => {
    languagesRequestFetchRequest();
  }, []);
  useEffect(() => {
    fetchAppConfig();
  }, [isLocalDBReady]);
  useEffect(() => {
    dispatch(
      "SET_LANGUAGES_CONFIG",
      languagesData?.languages?.map((language) => {
        return {
          name: language[1],
          id: language[0],
        };
      })
    );
  }, [languagesData?.length]);
  useEffect(() => {
    if (userID) console.log("Datastore elimited...");
  }, [authUserSub, serverWorkspaces?.length, userID]);

  const [readyForSubs, setReadyForSubscriptions] = useState(true);
  const { user, isIncognito, setIsIncognito, updateUser, fetchUser } =
    AppUser.useUser();
  const {
    isSupported,
    permission,
    requestPermission,
    sendNotification,
    error,
  } = AppNotification.useNotification();

  // Request permission when component mounts (for web notifications)
  useEffect(() => {
    if (!isElectronRunning()) {
      requestPermission();
    }
  }, []);

  const { dispatch, state } = AppStateManager.useAppStateManager();
  const sideMenuOpen = useReactiveVar(showSideMenu);
  const [_styles, setStyles] = useState(styles({ height, sideMenuOpen }));
  const userEmail = user?.user_email;

  useEffect(() => {
    if (state?.activeWorkspaceID && state?.isWorkspaceCollabReady !== true) {
      console.log("JOINING WORKSPACE");
      socket.joinWorkspace(state?.activeWorkspaceID, user?.username);
      socket.listenToNotifications((err, data) => {
        switch (data?.event) {
          case "init":
            dispatch("SET_WORKSPACE_ACTIVE_USERS", data?.msg?.users);
            dispatch("SET_WORKSPACE_COLLAB_READY", true);
            break;
          case "user-joined":
            dispatch("SET_WORKSPACE_ACTIVE_USERS", [
              ...state?.activeWorkspaceUsers,
              data?.msg,
            ]);
            break;
          case "user-left":
            const sItemIndex = state?.activeWorkspaceUsers
              .map((i) => i.id)
              .indexOf(data?.msg?.id);
            state?.activeWorkspaceUsers.splice(sItemIndex, 1);
            dispatch("SET_WORKSPACE_ACTIVE_USERS", state?.activeWorkspaceUsers);
          case "connect_error":
            if (state?.isWorkspaceCollabReady !== false)
              dispatch("SET_WORKSPACE_COLLAB_READY", false);
          default:
            break;
        }
        if (err) {
          return;
        }
      });
    }
    return () => {
      socket.leaveWorkspace(state?.activeWorkspaceID, user?.username);
    };
  }, [
    state?.activeWorkspaceID,
    _isConnectedToCloud,
    state?.isWorkspaceCollabReady,
  ]);

  useEffect(() => {
    if (state?.activeWorkspaceID) {
      loadEntities(state?.activeWorkspaceID);
      loadTrainingLogs(state?.activeWorkspaceID);
      loadAppLogs(state?.activeWorkspaceID);
      loadBatchLogs(state?.activeWorkspaceID);
      loadAgents(state?.activeWorkspaceID);
      loadCustomPrompts(state?.activeWorkspaceID);
      loadBenchmarks(state?.activeWorkspaceID);
      loadVirtualMachines(state?.activeWorkspaceID);
      loadDocuments(state?.activeWorkspaceID);
      loadExecEnvContainers(state?.activeWorkspaceID);
      loadBaseGenerationConfig();
    }
  }, [state?.activeWorkspaceID]);

  useEffect(() => {
    if (getActiveWorkspace()?.userID && user?.id && state?.activeWorkspaceID) {
      loadAccessCredentials();
    }
  }, [getActiveWorkspace()?.userID, user?.id, state?.activeWorkspaceID]);

  useEffect(() => {
    if (state?.activeWorkspaceID && state?.workspaces) {
      const workspace = state?.workspaces?.find(
        (wp) => wp.id === state?.activeWorkspaceID
      );
      if (workspace?.workspaceToken && workspace?.workspaceTokenID)
        dispatch("SET_WORKSPACE_READY", true);
    }
  }, [state?.activeWorkspaceID, state?.workspaces]);
  useEffect(() => {
    let workspaceSubscriptions = [];
    let isCancelled = false;

    const handleWorkspaceEvent = ({ value }) => {
      const updatedWorkspace = value?.data?.onUpdateWorkspaceEvent;
      const currentWorkspace = { ...getActiveWorkspace() };
      const isEventForActiveWorkspace =
        !!updatedWorkspace?.id && updatedWorkspace?.id === currentWorkspace?.id;

      if (
        isEventForActiveWorkspace &&
        updatedWorkspace?.workspaceToken &&
        updatedWorkspace?.workspaceTokenID &&
        !state?.isWorkspaceReady
      ) {
        dispatch("SET_WORKSPACE_READY", true);
      }
      if (updatedWorkspace?.activeUntil) {
        if (!isEventForActiveWorkspace) {
          handleStateUpdateFromSubMessage(
            { element: updatedWorkspace, opType: "UPDATE" },
            "workspaces",
            "SET_WORKSPACES"
          );
        }
      }

      if (
        // CRITICAL: only merge when the event is for the ACTIVE
        // workspace. This subscription watches every open workspace,
        // so events for OTHER workspaces (e.g. their periodic usage
        // updates) must never stamp their token onto this one — that
        // cross-wiring sent a foreign workspaceToken to the tfcode
        // sidecar, which the gateway rejects as
        // "Credits exhausted — recharge to continue."
        isEventForActiveWorkspace &&
        updatedWorkspace?.workspaceToken &&
        updatedWorkspace?.workspaceToken !== currentWorkspace?.workspaceToken
      ) {
        currentWorkspace.workspaceToken = updatedWorkspace?.workspaceToken;
        updateWorkspaceInContext(currentWorkspace);
      }
    };

    const setupSubscription = async () => {
      const openWorkspaces =
        state?.workspaces
          ?.filter(
            (u) =>
              (!u.workspaceToken ||
                !u.workspaceTokenID ||
                !u.activeUntil ||
                new Date(u?.activeUntil).getTime() < Date.now()) &&
              u?.userID === user?.id
          )
          ?.map((u) => u?.id) || [];
      const workspacesToWatch = [
        ...new Set(
          [state?.activeWorkspaceID, ...openWorkspaces].filter((u) => u)
        ),
      ];
      for (const workspaceId of workspacesToWatch) {
        try {
          const subscription = await subscribeGraphQL(
            graphqlOperation(onUpdateWorkspaceSocket, { id: workspaceId }),
            {
              next: handleWorkspaceEvent,
              error: (error) =>
                console.error("Subscription error on workspace:", error),
            }
          );
          if (isCancelled) {
            subscription?.unsubscribe?.();
            return;
          }
          if (subscription?.unsubscribe)
            workspaceSubscriptions.push(subscription);
        } catch (error) {
          console.error("Subscription error on workspace:", error);
        }
      }
    };
    setupSubscription();

    return () => {
      isCancelled = true;
      workspaceSubscriptions?.forEach((subscription) =>
        subscription?.unsubscribe?.()
      );
      workspaceSubscriptions = [];
    };
  }, [
    state?.activeWorkspaceID,
    state?.isWorkspaceReady,
    state?.workspaces?.length,
    getActiveWorkspace()?.nlpUrl,
    getActiveWorkspace()?.qaUrl,
  ]);
  const loadUsersWorkspace = async (activeWorkspaceID) => {
    let data;
    try {
      if (_isConnectedToCloud) {
        const result = await workspacesAPI.getWorkspaceUsers(activeWorkspaceID);
        data = result?.data?.getUsersWorkspace;
        const jsonValue = JSON.stringify(data);
        await AsyncStorage.setItem("workspaceUsers", jsonValue);
      } else {
        const stringValue = AsyncStorage.getItem("workspaceUsers");
        data = JSON.parse(stringValue);
      }
    } catch (error) {
      console.error("loadUsersWorkspace error", error);
    }
    dispatch("SET_WORKSPACE_USERS", data);
  };
  const loadPlanWorkspace = async (activeWorkspaceID) => {
    let data;
    try {
      if (_isConnectedToCloud) {
        const result = await workspacesAPI.getPlanWorkspace(activeWorkspaceID);
        data = result?.data?.getPlanWorkspace;
      }
    } catch (error) {
      console.error("loadPlanWorkspace error", error);
      window.location.replace(getWebAppUrl("/workspaces"));
    }
    dispatch("SET_WORKSPACE_PLAN", data);
  };
  const handleStateUpdateFromSubMessage = (msg, stateProperty, dispatchFn) => {
    const objectIndex = state[stateProperty]?.findIndex(
      (log) => log.id === msg?.element?.id
    );
    // console.log("MSG", msg, stateProperty, dispatchFn);
    try {
      switch (msg?.opType) {
        case "UPDATE":
          if (objectIndex >= 0) {
            let updatedProperty = [...state[stateProperty]];
            updatedProperty[objectIndex] = { ...msg?.element };
            dispatch(dispatchFn, updatedProperty);
          }
          break;
        case "INSERT":
          if (state[stateProperty]?.length > 0)
            dispatch(dispatchFn, [...state[stateProperty], msg?.element]);
          else dispatch(dispatchFn, [msg?.element]);
          break;
        case "DELETE":
          if (objectIndex >= 0) {
            state[stateProperty].splice(objectIndex, 1);
            dispatch(dispatchFn, [...state[stateProperty]]);
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Notification could not be managed", error);
    }
  };

  useEffect(() => {
    let sitesSubscription;
    const setupSubscription = async () => {
      const openSites = state?.sites
        ?.sort((a, b) => b._lastChangedAt - a._lastChangedAt) // Sort descending by _lastChangedAt
        ?.slice(0, 10)
        ?.map((u) => u?.id);

      if (state?.sites?.length > 0 && openSites?.filter((u) => u)?.length > 0) {
        sitesSubscription = await subscribeGraphQL(
          graphqlOperation(onUpdateSite, {
            filter: {
              and: [
                { id: { in: openSites } },
                { workspaceID: { eq: state?.activeWorkspaceID } },
              ],
            },
          }),
          {
            next: ({ value }) => {
              const updatedSite = value.data.onUpdateSite;
              handleStateUpdateFromSubMessage(
                { element: updatedSite, opType: "UPDATE" },
                "sites",
                "SET_SITES"
              );
            },
            error: (error) =>
              console.error("Subscription error on sites:", error),
          }
        );
      }
    };
    setupSubscription();

    return () => {
      if (sitesSubscription) {
        sitesSubscription?.unsubscribe();
      }
    };
  }, [JSON.stringify(state?.sites), state?.activeWorkspaceID]);

  useEffect(() => {
    let trainingLogSubscription;
    const setupSubscription = async () => {
      const openLogs = state?.trainingLogs
        ?.filter(
          (u) =>
            u.status === "dispatched" ||
            u.status === "inProgress" ||
            u.status === "cancellationRequested" ||
            u.status === "swapping" ||
            u.status === "swapModelRequested" ||
            u.status === "fineTuningRequested" ||
            u.status === "training"
        )
        ?.sort((a, b) => b._lastChangedAt - a._lastChangedAt) // Sort descending by _lastChangedAt
        ?.slice(0, 3)
        ?.map((u) => u?.id);

      if (
        state?.trainingLogs?.filter((u) => u)?.length > 0 &&
        openLogs?.filter((u) => u)?.length > 0
      ) {
        trainingLogSubscription = await subscribeGraphQL(
          graphqlOperation(onUpdateTrainingLog, {
            filter: {
              and: [
                { id: { in: openLogs } },
                { workspaceID: { eq: state?.activeWorkspaceID } },
              ],
            },
          }),
          {
            next: ({ value }) => {
              const updatedLog = value.data.onUpdateTrainingLog;
              handleStateUpdateFromSubMessage(
                { element: updatedLog, opType: "UPDATE" },
                "trainingLogs",
                "SET_TRAINING_LOGS"
              );
            },
            error: (error) =>
              console.error("Subscription error on training:", error),
          }
        );
      }
    };
    setupSubscription();
    return () => {
      if (trainingLogSubscription) {
        trainingLogSubscription?.unsubscribe();
      }
    };
  }, [JSON.stringify(state?.trainingLogs), state?.activeWorkspaceID]);

  useEffect(() => {
    let notificationSubscription;

    const setupSubscription = async () => {
      if (state?.activeWorkspaceID && user?.id) {
        notificationSubscription = await subscribeGraphQL(
          graphqlOperation(onCreateUserNotification, {
            filter: {
              and: [
                { workspaceID: { eq: state?.activeWorkspaceID } },
                { userID: { eq: user?.id } },
              ],
            },
          }),
          {
            next: ({ value }) => {
              const newNotification = value.data.onCreateUserNotification;
              if (!newNotification) return;
              try {
                const existing = userNotifications() || [];
                userNotifications([
                  { ...newNotification, _read: false },
                  ...existing,
                ].slice(0, 50));
              } catch (e) {
                console.error("Failed to queue notification:", e);
              }
              sendNotification(newNotification?.title || "Notification", {
                body: newNotification?.description || "",
                tag: newNotification?.id,
                data: newNotification?.metadata || {},
                onClick: () => {
                  try {
                    const meta =
                      typeof newNotification?.metadata === "string"
                        ? JSON.parse(newNotification.metadata)
                        : newNotification?.metadata || {};
                    if (meta?.chatId && state?.activeWorkspaceID) {
                      const url = `/workspaces/${state.activeWorkspaceID}/chats/${meta.chatId}`;
                      if (typeof window !== "undefined" && window.location) {
                        window.location.assign(url);
                      }
                    }
                  } catch (e) {
                    console.error("Notification click nav failed:", e);
                  }
                },
              });
            },
            error: (error) =>
              console.error("Subscription error on notification:", error),
          }
        );
      }
    };

    if (state?.activeWorkspaceID && user?.id) {
      setupSubscription();
      // sendNotification("Test", {
      //   body: "This is a test notification",
      //   icon: "/path/to/icon.png",
      //   requireInteraction: true,
      //   onClick: () => {
      //     console.log("Notification clicked!");
      //   },
      // });
    }

    return () => {
      if (notificationSubscription && notificationSubscription?.unsubscribe) {
        notificationSubscription?.unsubscribe();
      }
    };
  }, [state?.activeWorkspaceID, user?.id]);

  useEffect(() => {
    let batchLogsSubscription;
    let batchLogUpdateSubscription;
    const setupSubscription = async () => {
      if (state?.activeWorkspaceID) {
        batchLogsSubscription = await subscribeGraphQL(
          graphqlOperation(onCreateBatchJobLog, {
            filter: { workspaceID: { eq: state?.activeWorkspaceID } },
          }),
          {
            next: ({ value }) => {
              const newBatchLog = value.data.onCreateBatchJobLog;
              handleStateUpdateFromSubMessage(
                { element: newBatchLog, opType: "INSERT" },
                "batchLogs",
                "SET_BATCH_LOGS"
              );
            },
            error: (error) =>
              console.error("Subscription error on batch creation:", error),
          }
        );

        // You might also want to subscribe to batch log updates
        batchLogUpdateSubscription = await subscribeGraphQL(
          graphqlOperation(onUpdateBatchJobLog, {
            filter: { workspaceID: { eq: state?.activeWorkspaceID } },
          }),
          {
            next: ({ value }) => {
              const updatedBatchLog = value.data.onUpdateBatchJobLog;
              handleStateUpdateFromSubMessage(
                { element: updatedBatchLog, opType: "UPDATE" },
                "batchLogs",
                "SET_BATCH_LOGS"
              );
            },
            error: (error) =>
              console.error("Subscription error on batch:", error),
          }
        );
      }
      setupSubscription();

      return () => {
        if (batchLogsSubscription && batchLogsSubscription?.unsubscribe)
          batchLogsSubscription?.unsubscribe();
        if (
          batchLogUpdateSubscription &&
          batchLogUpdateSubscription?.unsubscribe
        )
          batchLogUpdateSubscription?.unsubscribe();
      };
    };
  }, [state?.activeWorkspaceID, JSON.stringify(state?.batchLogs)]);

  useEffect(() => {
    if (getActiveWorkspace()?.id) {
      loadUsersWorkspace(getActiveWorkspace()?.id);
      loadPlanWorkspace(getActiveWorkspace()?.id);
    }
  }, [getActiveWorkspace()?.id, _isConnectedToCloud, _lastMembersChange]);

  useEffect(() => {
    pinnedSideMenu(width > Constants.screenResponsiveWidth);
  }, [width]);

  useEffect(() => {
    if (!forcedClosed()) showSideMenu(width > Constants.screenResponsiveWidth);
  }, [width]);

  useEffect(() => {
    appDimensions({ width, height });
  }, [width, height]);

  useEffect(() => {
    setStyles(styles({ width, height, sideMenuOpen }));
  }, [sideMenuOpen, width, height]);

  const handleThemeSelect = (theme) => {
    setMode(theme);
  };

  const loadWorkspacesInProgressRef = useRef(false);
  const loadWorkspaces = async (loggedUser) => {
    if (loadWorkspacesInProgressRef.current) return;
    loadWorkspacesInProgressRef.current = true;
    try {
      dispatch("SET_WORKSPACES_LOADING", true);
      const workSpaces = await workspacesHelper.getWorkspacesByUserID(
        loggedUser?.id
      );
      const cleanedWorkspaces = [
        ...workSpaces?.map((u) => u?.data?.getWorkspace),
      ]?.filter((u) => u && u?._deleted != true);
      dispatch("SET_WORKSPACES", cleanedWorkspaces);
      // Unblock the workspaces screen immediately — exact UoI consumption is
      // fetched per-workspace by WorkspacesScreenContainer, which shows a
      // translucent loading bar in each card until the exact value resolves.
      dispatch("SET_WORKSPACES_LOADING", false);
    } catch (error) {
      console.error("loadUsersWorkspace", error);
      AppToasty.show(t("workspacesFailedToLoad"), { type: "danger" });
      dispatch("SET_WORKSPACES_LOADING", false);
    } finally {
      loadWorkspacesInProgressRef.current = false;
    }
  };
  const loadBaseGenerationConfig = async () => {
    const baseGenerationConfig = await helper.getGenerationConfig();
    dispatch(
      "SET_GENERATION_CONFIG_DATA",
      baseGenerationConfig?.data?.listGenerationConfigs?.items[0]
    );
  };
  const loadTrainingLogs = async (workspaceID) => {
    let resultLogs = await trainingLogHelper.getTrainingLogsByWorkspaceID(
      workspaceID
    );
    resultLogs = resultLogs?.data?.trainingLogsByWorkspace?.items?.filter(
      (u) => u?._deleted != true
    );
    dispatch("SET_TRAINING_LOGS", resultLogs);
  };

  const loadCustomPrompts = async (workspaceID) => {
    let resultPrompts = await Prompts.getCustomPromptsByWorkspaceID(
      workspaceID
    );
    resultPrompts =
      resultPrompts?.data?.customPromptsByWorkspace?.items?.filter(
        (u) => u?._deleted != true
      );
    dispatch("SET_CUSTOM_PROMPTS", resultPrompts);
  };
  const loadBatchLogs = async (workspaceID) => {
    let resultLogs = await batchLogHelper?.getBatchLogsByWorkspaceID(
      workspaceID
    );
    resultLogs = resultLogs?.data?.batchJobLogsByWorkspace?.items?.filter(
      (u) => u?._deleted != true
    );
    resultLogs = resultLogs?.map((u) => {
      return {
        ...u,
        batchMeta: JSON.parse(u?.batchMeta),
        batchConfig: JSON.parse(u?.batchConfig),
      };
    });
    dispatch("SET_BATCH_LOGS", resultLogs);
  };
  const loadBenchmarks = async (workspaceID) => {
    try {
      let resultBenchmarks = await benchmarksHelper?.getBenchmarksByWorkspaceID(
        workspaceID
      );
      resultBenchmarks =
        resultBenchmarks?.data?.BenchmarksByWorkspace?.items?.filter(
          (u) => u?._deleted != true
        );
      dispatch("SET_BENCHMARKS", resultBenchmarks);
    } catch (error) {
      console.error("Error loading benchmarks:", error);
    } finally {
    }
  };
  const loadVirtualMachines = async (workspaceID) => {
    try {
      let resultVM =
        await virtualMachineHelper?.getVirtualMachinesByWorkspaceID(
          workspaceID
        );
      resultVM = resultVM?.data?.virtualMachinesByWorkspace?.items?.filter(
        (u) => u?._deleted != true
      );
      dispatch("SET_VMS", resultVM);
    } catch (error) {
      console.error("Error loading virtual machines:", error);
    } finally {
    }
  };
  const loadExecEnvContainers = async (workspaceID) => {
    try {
      let resultVM =
        await customExecutionContainersHelper.getCustomExecutionEnvironmentsByWorkspaceID(
          workspaceID
        );
      resultVM =
        resultVM?.data?.customExecutionEnvironmentByWorkspace?.items?.filter(
          (u) => u?._deleted != true
        );
      dispatch("SET_EXEC_ENVS_CONTAINERS", resultVM);
    } catch (error) {
      console.error(
        "Error loading custom execution environments machines:",
        error
      );
    } finally {
    }
  };
  const loadDocuments = async (workspaceID) => {
    const promise1 = fetchMRCData({
      workspaceID,
      type: "readComprehensionContent",
      fields: [
        "id",
        "title",
        "topics",
        "embedding_status",
        "external_path",
        "status",
      ],
      page: -1,
      pageLimit: 5000,
    });

    const promise2 = fetchMRCData({
      workspaceID,
      type: "questionAnswering",
      fields: [
        "id",
        "title",
        "topics",
        "embedding_status",
        "external_path",
        "status",
      ],
      page: -1,
      pageLimit: 5000,
    });

    const promise3 = fetchMRCData({
      workspaceID,
      type: "readComprehensionPdf",
      fields: [
        "id",
        "title",
        "topics",
        "embedding_status",
        "external_path",
        "status",
      ],
      page: -1,
      pageLimit: 5000,
    });
    const promise4 = fetchMRCData({
      workspaceID,
      type: "readComprehensionFile",
      fields: [
        "id",
        "title",
        "topics",
        "embedding_status",
        "external_path",
        "status",
      ],
      page: -1,
      pageLimit: 5000,
    });
    const promise5 = fetchMRCData({
      workspaceID,
      type: "designArtifact",
      fields: [
        "id",
        "title",
        "topics",
        "embedding_status",
        "external_path",
        "type",
        "status",
      ],
      page: -1,
      pageLimit: 5000,
    });

    const [result, qaResult, pdfResult, fileResult, artifactResult] =
      await Promise.all([promise1, promise2, promise3, promise4, promise5]);
    const _docs = [
      ...result?.data,
      ...qaResult?.data,
      ...pdfResult?.data,
      ...fileResult?.data,
      ...artifactResult?.data,
    ];
    dispatch(
      "SET_EMBEDDED_MRC_DOCUMENTS",
      _docs?.filter((u) => u?.embedding_status === "ready")
    );
    dispatch("SET_MRC_DOCUMENTS", _docs);
  };

  const loadEntities = async (workspaceID) => {
    let aiEntities = await entitiesHelper.getEntitiesByWorkspaceID(workspaceID);
    aiEntities = aiEntities?.data?.entitiesByWorkspace?.items?.filter(
      (u) => u?._deleted != true
    );
    dispatch("SET_AI_ENTITIES_CONFIG", aiEntities);
  };

  const loadAppLogs = async (workspaceID) => {
    // let appLogs = await appLogHelper.getAppLogsByWorkspaceID(workspaceID);
    // appLogs = appLogs?.data?.appLogsByWorkspace?.items?.filter(
    //   (u) => u?._deleted != true
    // );
    // dispatch("SET_APP_LOGS", appLogs);
    dispatch("SET_APP_LOGS_READY", true);
  };
  const loadAccessCredentials = async () => {
    let userFound = false;
    if (user.id === getActiveWorkspace()?.userID) {
      userType(UserType.ROOT);
      userFound = true;
    }

    const result = await makeGraphQLRequest(
      graphqlOperation(userToWorkspaceMapping, {
        workspaceID: getActiveWorkspace()?.id,
        userID: {
          eq: user.id,
        },
      })
    );
    dispatch(
      "SET_ACCESS_CREDENTIALS",
      result?.data?.userToWorkspaceMapping?.items?.filter(
        (u) => u?._deleted != true
      )
    );
    const config = result?.data?.userToWorkspaceMapping?.items?.filter(
      (u) => u?._deleted != true
    );
    if (userFound == false) {
      if (config?.length > 0) {
        userType(config[0].userType);
      } else {
        userType(UserType.USER);
      }
    }

    console.log("user logged in of type", userType());
  };
  const handleUserInitiation = useCallback(async () => {
    if (isLocalDBReady && authUserSub) {
      let loggedUser = await usersAPI.getUserByOwner(authUserSub);
      loggedUser = loggedUser?.data?.usersByOwner?.items?.filter(
        (u) => u?._deleted != true
      );

      if (loggedUser?.length > 0 && loggedUser[0]) {
        setIsIncognito(!loggedUser[0]?.isOnboarded);
        updateUser({
          ...user,
          ...loggedUser[0],
          cognitoUsername: user.username,
          stripeCustomerData,
          userHelperPreferences: JSON.parse(
            loggedUser[0]?.userHelperPreferences
          ),
        });
        handleThemeSelect(loggedUser[0].theme);
        if (loggedUser[0].language) changeLanguage(loggedUser[0].language);
        loadWorkspaces(loggedUser[0]);
        setPreferencesLoaded(true);
      } else setIsIncognito(true);
    }
  }, [isLocalDBReady, authUserSub]);

  useEffect(() => {
    if (user?.user_email) {
      handleUserInitiation();
    }
  }, [isLocalDBReady, user?.user_email, handleUserInitiation]);

  useEffect(() => {
    if (currentView === "mainNav") {
      fetchUser();
    }
  }, [currentView]);

  const mainViewScreen = useMemo(() => {
    return (
      <MainViewScreen height={height} width={width} sideMenuOpen={sideMenuOpen}>
        {children}
      </MainViewScreen>
    );
  }, [width, height, sideMenuOpen, children]);
  return isIncognito && isLocalDBReady ? (
    <OnboardingScreen
      user={user}
      themeMode={mode}
      dispatch={dispatch}
      handleThemeSelect={handleThemeSelect}
      setIsIncognito={setIsIncognito}
      updateUser={updateUser}
    />
  ) : !isConnectedToCloud() ||
    (!isIncognito && isLocalDBReady && preferencesLoaded) ? (
    <AppBaseScreen isScrollable={Platform.OS === "web" && false}>
      <View style={_styles.container}>
        <Router>
          {Platform.OS !== "web" && <DeepLinking />}
          {mainViewScreen}
        </Router>
      </View>
    </AppBaseScreen>
  ) : (
    <ToothFairyLoadingPlaceholder
      caption="messages.settingUp"
      brandedAnimation
    />
  );
};

export default MainViewContainer;

const styles = ({ height, sideMenuOpen, width }) =>
  StyleSheet.create({
    container: {
      height: "100%",
      flexDirection: "row",
      width: "100%",
      justifyContent: "flex-start",
      alignItems: "flex-start",
      overflow: "hidden",
    },
    content: {
      height: height - 46,
      maxHeight: height - 46,
      width: "100%",
      maxWidth: width - (sideMenuOpen ? 150 : 0),
    },
  });
